import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, ChevronRight, Video, Sun, Plane, ListChecks, UserPlus } from "lucide-react";

const TYPE_CFG = {
  meeting:     { color: "bg-cyan-500",    bgLight: "bg-cyan-50 text-cyan-800 border-cyan-200",   icon: Video,   label: "Meeting" },
  holiday:     { color: "bg-red-500",     bgLight: "bg-red-50 text-red-800 border-red-200",     icon: Sun,     label: "Holiday" },
  leave:       { color: "bg-violet-500",  bgLight: "bg-violet-50 text-violet-800 border-violet-200", icon: Plane, label: "Leave" },
  task:        { color: "bg-amber-500",   bgLight: "bg-amber-50 text-amber-800 border-amber-200", icon: ListChecks, label: "Task due" },
  onboarding:  { color: "bg-emerald-500", bgLight: "bg-emerald-50 text-emerald-800 border-emerald-200", icon: UserPlus, label: "Onboarding" },
};

export default function CalendarPage() {
  const now = new Date();
  const [ym, setYm] = useState({ y: now.getFullYear(), m: now.getMonth() + 1 });
  const [events, setEvents] = useState([]);
  const [view, setView] = useState("month"); // month | week | day
  const [cursor, setCursor] = useState(new Date());

  const monthStart = useMemo(() => new Date(Date.UTC(ym.y, ym.m - 1, 1)).toISOString(), [ym]);
  const monthEnd = useMemo(() => {
    const d = ym.m === 12 ? new Date(Date.UTC(ym.y + 1, 0, 1)) : new Date(Date.UTC(ym.y, ym.m, 1));
    return d.toISOString();
  }, [ym]);

  useEffect(() => {
    api.get("/calendar/events", { params: { from: monthStart, to: monthEnd } })
      .then((r) => setEvents(r.data))
      .catch(() => setEvents([]));
  }, [monthStart, monthEnd]);

  const monthName = new Date(ym.y, ym.m - 1).toLocaleString("en", { month: "long" });
  const firstDay = new Date(ym.y, ym.m - 1, 1).getDay();
  const daysInMonth = new Date(ym.y, ym.m, 0).getDate();
  const prev = () => setYm(({ y, m }) => m === 1 ? { y: y-1, m: 12 } : { y, m: m-1 });
  const next = () => setYm(({ y, m }) => m === 12 ? { y: y+1, m: 1 } : { y, m: m+1 });

  const eventsByDay = useMemo(() => {
    const byDay = {};
    events.forEach((ev) => {
      const s = new Date(ev.start);
      const e = ev.end ? new Date(ev.end) : s;
      for (let d = new Date(Date.UTC(s.getUTCFullYear(), s.getUTCMonth(), s.getUTCDate())); d <= e; d.setUTCDate(d.getUTCDate() + 1)) {
        if (d.getUTCMonth() !== ym.m - 1 || d.getUTCFullYear() !== ym.y) continue;
        const day = d.getUTCDate();
        (byDay[day] ||= []).push(ev);
      }
    });
    return byDay;
  }, [events, ym]);

  const weekDays = useMemo(() => {
    const startOfWeek = new Date(cursor);
    startOfWeek.setDate(cursor.getDate() - cursor.getDay());
    return Array.from({ length: 7 }).map((_, i) => {
      const d = new Date(startOfWeek); d.setDate(startOfWeek.getDate() + i); return d;
    });
  }, [cursor]);

  const eventsOnDate = (d) => {
    const y = d.getFullYear(), m = d.getMonth(), day = d.getDate();
    return events.filter((ev) => {
      const s = new Date(ev.start), e = ev.end ? new Date(ev.end) : s;
      return s.getFullYear() <= y && e.getFullYear() >= y
        && (new Date(y, m, day) >= new Date(s.getFullYear(), s.getMonth(), s.getDate()))
        && (new Date(y, m, day) <= new Date(e.getFullYear(), e.getMonth(), e.getDate()));
    });
  };

  const types = ["meeting", "holiday", "leave", "task", "onboarding"];

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5" data-testid="calendar-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">Calendar</div>
          <h1 className="text-3xl font-[Outfit] font-semibold tracking-tight text-slate-900">{view === "month" ? `${monthName} ${ym.y}` : "Calendar"}</h1>
          <p className="text-sm text-slate-600 mt-1">All meetings, holidays, leaves & task due dates in one place</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex rounded-md border border-slate-200 overflow-hidden">
            {["month", "week", "day"].map(v => (
              <button key={v} onClick={() => setView(v)}
                className={`px-3 py-1.5 text-xs capitalize ${view === v ? "bg-slate-900 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}
                data-testid={`cal-view-${v}`}>
                {v}
              </button>
            ))}
          </div>
          {view === "month" && (
            <>
              <Button variant="outline" size="icon" onClick={prev} className="h-8 w-8"><ChevronLeft className="w-4 h-4"/></Button>
              <Button variant="outline" size="sm" onClick={() => setYm({ y: now.getFullYear(), m: now.getMonth() + 1 })}>Today</Button>
              <Button variant="outline" size="icon" onClick={next} className="h-8 w-8"><ChevronRight className="w-4 h-4"/></Button>
            </>
          )}
          {view !== "month" && (
            <>
              <Button variant="outline" size="icon" onClick={() => { const d = new Date(cursor); d.setDate(d.getDate() - (view === "week" ? 7 : 1)); setCursor(d); }} className="h-8 w-8"><ChevronLeft className="w-4 h-4"/></Button>
              <Button variant="outline" size="sm" onClick={() => setCursor(new Date())}>Today</Button>
              <Button variant="outline" size="icon" onClick={() => { const d = new Date(cursor); d.setDate(d.getDate() + (view === "week" ? 7 : 1)); setCursor(d); }} className="h-8 w-8"><ChevronRight className="w-4 h-4"/></Button>
            </>
          )}
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 text-xs">
        {types.map(t => {
          const cfg = TYPE_CFG[t];
          const Ico = cfg.icon;
          return (
            <div key={t} className={`flex items-center gap-1.5 px-2 py-1 rounded-md border ${cfg.bgLight}`}>
              <Ico className="w-3 h-3"/>
              <span>{cfg.label}</span>
            </div>
          );
        })}
      </div>

      {view === "month" && (
        <Card className="p-3 border-slate-200 shadow-none">
          <div className="grid grid-cols-7 gap-1">
            {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => (
              <div key={d} className="text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500 text-center py-1">{d}</div>
            ))}
            {Array(firstDay).fill(0).map((_, i) => <div key={`pad-${i}`} className="min-h-[90px] bg-slate-50/50 rounded"/>)}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const isToday = day === now.getDate() && ym.m === now.getMonth() + 1 && ym.y === now.getFullYear();
              const dayEvents = eventsByDay[day] || [];
              return (
                <div key={day} className={`min-h-[90px] rounded border ${isToday ? "border-cyan-500 bg-cyan-50/40" : "border-slate-200"} p-1.5`}>
                  <div className={`text-xs font-semibold mb-1 ${isToday ? "text-cyan-700" : "text-slate-600"}`}>{day}</div>
                  <div className="space-y-0.5">
                    {dayEvents.slice(0, 3).map((ev) => {
                      const cfg = TYPE_CFG[ev.type] || TYPE_CFG.meeting;
                      return (
                        <div key={`${ev.id}-${day}`} className={`text-[10px] px-1 py-0.5 rounded border truncate ${cfg.bgLight}`} title={ev.title}>
                          {ev.title}
                        </div>
                      );
                    })}
                    {dayEvents.length > 3 && <div className="text-[10px] text-slate-500">+{dayEvents.length - 3} more</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {view === "week" && (
        <Card className="p-3 border-slate-200 shadow-none overflow-x-auto">
          <div className="grid grid-cols-7 gap-2 min-w-[700px]">
            {weekDays.map((d) => {
              const dayEv = eventsOnDate(d);
              const isToday = d.toDateString() === now.toDateString();
              return (
                <div key={d.toISOString()} className={`rounded border ${isToday ? "border-cyan-500 bg-cyan-50/40" : "border-slate-200"} p-2 min-h-[200px]`}>
                  <div className="text-[10px] uppercase text-slate-500 mb-0.5">{d.toLocaleString([], {weekday: "short"})}</div>
                  <div className="text-lg font-[Outfit] font-semibold text-slate-900 mb-2">{d.getDate()}</div>
                  <div className="space-y-1">
                    {dayEv.map((ev) => {
                      const cfg = TYPE_CFG[ev.type] || TYPE_CFG.meeting;
                      return (
                        <div key={ev.id} className={`text-[11px] px-2 py-1 rounded border ${cfg.bgLight}`}>
                          <div className="font-medium line-clamp-1">{ev.title}</div>
                          <div className="text-[10px] opacity-75">{cfg.label}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {view === "day" && (
        <Card className="p-5 border-slate-200 shadow-none">
          <div className="text-lg font-[Outfit] font-semibold text-slate-900 mb-3">
            {cursor.toLocaleDateString([], { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
          </div>
          <div className="space-y-2">
            {eventsOnDate(cursor).length === 0 && <div className="text-sm text-slate-500">Nothing on this day.</div>}
            {eventsOnDate(cursor).map((ev) => {
              const cfg = TYPE_CFG[ev.type] || TYPE_CFG.meeting;
              const Ico = cfg.icon;
              return (
                <div key={ev.id} className={`flex items-start gap-3 p-3 rounded-md border ${cfg.bgLight}`}>
                  <Ico className="w-4 h-4 mt-0.5"/>
                  <div className="flex-1">
                    <div className="text-sm font-medium">{ev.title}</div>
                    <div className="text-xs opacity-75 mt-0.5">
                      {cfg.label} · {new Date(ev.start).toLocaleString([], { hour: "2-digit", minute: "2-digit" })}
                      {ev.end && ev.end !== ev.start && ` – ${new Date(ev.end).toLocaleString([], { hour: "2-digit", minute: "2-digit" })}`}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Quick stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        {types.map(t => {
          const count = events.filter(e => e.type === t).length;
          const cfg = TYPE_CFG[t];
          return (
            <Card key={t} className={`p-3 border-slate-200 shadow-none`}>
              <div className="text-[10px] uppercase tracking-wider text-slate-500">{cfg.label}</div>
              <div className="text-xl font-[Outfit] font-semibold text-slate-900 mt-0.5">{count}</div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
